import React, { FC, Fragment } from 'react';
import { ActivityLogRecord } from 'types/ActivityLog';
import { formatDateTime } from 'app/utils/date';
import RecordSingleChange from 'app/modules/posts/modules/post/components/postCommunication/activityLog/activityRecord/RecordSingleChange';
import {
  formatActivityLogPostChangeMessage,
  getWorkflowStatusValueFromRecordChange,
} from 'app/modules/posts/modules/post/components/postCommunication/activityLog/utils';
import WorkflowStatusBadge from 'app/components/workflowStatus/WorkflowStatusBadge';
import clsx from 'clsx';
import ActivityLogSingleRecordWrapper from './ActivityLogSingleRecordWrapper';
import DemoBadge from 'app/components/DemoBadge';
import StringUtils from 'utils/string';

type Props = {
  data: ActivityLogRecord[];
};

const ActivityLogRecordList: FC<Props> = ({ data }) => {
  const renderSingleRecord = (record: ActivityLogRecord, index: number) => {
    const isSingleChangeRecord = record.changes.length === 1;
    const changedStatusRecord = getWorkflowStatusValueFromRecordChange(record);

    return (
      <>
        {changedStatusRecord && (
          <ActivityLogSingleRecordWrapper
            classNames={{
              line: clsx('-tw-bottom-9', {
                'tw-top-4': index === 0,
              }),
              indicator: 'tw-bg-white tw-ring-grayscale-50',
            }}
          >
            <div className="tw-flex tw-h-8 tw-items-center">
              <WorkflowStatusBadge status={changedStatusRecord} />
            </div>
          </ActivityLogSingleRecordWrapper>
        )}
        <ActivityLogSingleRecordWrapper
          classNames={{
            line: clsx({
              'tw-top-4': index === 0,
              '-tw-bottom-9': index !== data.length - 1,
            }),
            indicator: 'tw-bg-success-100 tw-ring-success-100',
          }}
        >
          <div className="tw-ml-1 tw-flex  tw-flex-col tw-justify-center tw-text-sm">
            <span
              className="tw-text-xs tw-text-grayscale-100"
              data-cy="activity-log-single-record-datetime"
            >
              {formatDateTime(record.timestamp)}
            </span>
            <div
              className="tw-flex tw-flex-wrap tw-items-start tw-gap-1 tw-font-semibold"
              data-cy="activity-log-single-record-change-title"
            >
              <span className="tw-text-grayscale-180">
                {StringUtils.formatEmailToUsername(record.user.name)}
              </span>
              {isSingleChangeRecord ? (
                <span className="tw-text-success-100">
                  <RecordSingleChange
                    change={record.changes[0]}
                    groupType={record.groupType}
                  />
                </span>
              ) : (
                <span className="tw-text-success-100">
                  {formatActivityLogPostChangeMessage(record)}
                </span>
              )}
              {record.user.is_demo && <DemoBadge variant="light" />}
            </div>
            {!isSingleChangeRecord && (
              <div
                className="tw-mt-1 tw-text-grayscale-180"
                data-cy="activity-log-single-record-changes-list"
              >
                {record.changes.map((change, index) => (
                  <Fragment key={index}>
                    <RecordSingleChange
                      change={change}
                      groupType={record.groupType}
                    />
                    {index !== record.changes.length - 1 && <span>, </span>}
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        </ActivityLogSingleRecordWrapper>
      </>
    );
  };

  return (
    <ul
      className="tw-space-y-4"
      data-name="activity-log-records-list"
      data-cy="activity-log-records-list"
    >
      {data.map((record, index) => (
        <Fragment key={index}>{renderSingleRecord(record, index)}</Fragment>
      ))}
    </ul>
  );
};

export default ActivityLogRecordList;
